import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
  VStack,
  Text,
  Badge,
  Card,
  CardBody,
  CardHeader,
  SimpleGrid,
  Icon,
  Divider,
  useColorModeValue,
  Collapse,
  Tooltip,
  HStack,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon, SearchIcon, ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { MdStore, MdDevices, MdEdit, MdAssignment } from "react-icons/md";

const DeviceAssignment = () => {
  const [devices, setDevices] = useState([]);
  const [filteredDevices, setFilteredDevices] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [expandedStores, setExpandedStores] = useState(new Set());
  const [deviceData, setDeviceData] = useState({
    name: "",
    identifier: "",
    type: "",
  });
  const toast = useToast();

  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const cardBg = useColorModeValue("gray.50", "gray.700");
  const textColor = useColorModeValue("gray.700", "gray.200");

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user && user.devices) {
      setDevices(user.devices);
      setFilteredDevices(user.devices);
      setLoading(false);
    }
  }, []);

  // Group devices by store
  const devicesByStore = filteredDevices.reduce((acc, device) => {
    const storeId = device.store?.id || 'unassigned';
    const storeName = device.store?.name || 'Unassigned Devices';
    
    if (!acc[storeId]) {
      acc[storeId] = {
        storeName,
        storeId,
        devices: [],
        merchantName: device.merchant?.name || 'Your Store'
      };
    }
    acc[storeId].devices.push(device);
    return acc;
  }, {});

  const handleAddClick = () => {
    setDeviceData({
      name: "",
      identifier: "",
      type: "",
    });
    setIsModalOpen(true);
  };

  const handleDeleteClick = async (itemId) => {
    const updatedDevices = devices.filter((item) => item.id !== itemId);
    setDevices(updatedDevices);
    setFilteredDevices(updatedDevices);
    const user = JSON.parse(localStorage.getItem("user"));
    user.devices = updatedDevices;
    localStorage.setItem("user", JSON.stringify(user));

    toast({
      title: "Device deleted.",
      description: "The device has been deleted successfully.",
      status: "success",
      duration: 5000,
      isClosable: true,
    });
  };

  const handleSearch = (event) => {
    const filter = event.target.value.toLowerCase();
    setSearchQuery(filter);
    if (!filter) {
      setFilteredDevices(devices);
      return;
    }
    const filtered = devices.filter(
      (item) =>
        item.name.toLowerCase().includes(filter) ||
        item.identifier.toLowerCase().includes(filter) ||
        (item.type && item.type.name.toLowerCase().includes(filter)) ||
        (item.store && item.store.name.toLowerCase().includes(filter))
    );
    setFilteredDevices(filtered);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleModalSubmit = async () => {
    const newDevice = { ...deviceData, id: Date.now() };
    const updatedDevices = [...devices, newDevice];
    setDevices(updatedDevices);
    setFilteredDevices(updatedDevices);

    const user = JSON.parse(localStorage.getItem("user"));
    user.devices = updatedDevices;
    localStorage.setItem("user", JSON.stringify(user));

    toast({
      title: "Device created.",
      description: "The device has been created successfully.",
      status: "success",
      duration: 5000,
      isClosable: true,
    });

    handleModalClose();
  };

  const toggleStoreExpansion = (storeId) => {
    const newExpanded = new Set(expandedStores);
    if (newExpanded.has(storeId)) {
      newExpanded.delete(storeId);
    } else {
      newExpanded.add(storeId);
    }
    setExpandedStores(newExpanded);
  };

  const getDeviceTypeColor = (deviceType) => {
    const typeColors = {
      'kiosk': 'blue',
      'reader': 'green',
      'printer': 'purple',
      'display': 'orange',
      'default': 'gray'
    };
    return typeColors[deviceType?.toLowerCase()] || typeColors.default;
  };

  const getAssignmentStatus = (device) => {
    if (device.store) {
      return { status: 'assigned', color: 'green', text: `Assigned to ${device.store.name}` };
    }
    return { status: 'unassigned', color: 'red', text: 'Unassigned' };
  };

  return (
    <>
      <Box>
        <Flex
          justify={"space-between"}
          alignItems={"center"}
          width={"100%"}
          flexWrap={"wrap"}
          mt={4}
        >
          <VStack align="start" spacing={1}>
            <Heading size={"xl"} letterSpacing={-2}>
              Device Assignment
            </Heading>
            <Text color="gray.500" fontSize="sm">
              Manage and assign devices to your stores
            </Text>
          </VStack>
          <Button
            leftIcon={<AddIcon />}
            bg="black"
            color={"white"}
            _hover={{
              color: "black",
              bg: "white",
              borderWidth: "1px",
              borderColor: "gray.300",
            }}
            variant="outline"
            onClick={handleAddClick}
          >
            Add Device
          </Button>
        </Flex>
        
        <Box m={4}>
          <InputGroup size={"md"} maxW="400px">
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.300" />
            </InputLeftElement>
            <Input
              type="search"
              placeholder="Search devices or stores..."
              _placeholder={{ color: "gray.500" }}
              onChange={handleSearch}
              value={searchQuery}
            />
          </InputGroup>
        </Box>
      </Box>

      <VStack spacing={6} align="stretch">
        {Object.values(devicesByStore).map((storeGroup) => (
          <Card key={storeGroup.storeId} bg={bgColor} border="1px" borderColor={borderColor}>
            <CardHeader>
              <Flex justify="space-between" align="center">
                <Flex align="center" gap={3}>
                  <Icon as={MdStore} color="blue.500" boxSize={5} />
                  <VStack align="start" spacing={0}>
                    <Heading size="md" color={textColor}>
                      {storeGroup.storeName}
                    </Heading>
                    <Text fontSize="sm" color="gray.500">
                      {storeGroup.merchantName}
                    </Text>
                  </VStack>
                </Flex>
                <Flex align="center" gap={3}>
                  <Badge colorScheme="blue" variant="subtle">
                    {storeGroup.devices.length} Device{storeGroup.devices.length !== 1 ? 's' : ''}
                  </Badge>
                  <IconButton
                    icon={expandedStores.has(storeGroup.storeId) ? <ChevronUpIcon /> : <ChevronDownIcon />}
                    onClick={() => toggleStoreExpansion(storeGroup.storeId)}
                    variant="ghost"
                    size="sm"
                    aria-label="Toggle store devices"
                  />
                </Flex>
              </Flex>
            </CardHeader>
            
            <Collapse in={expandedStores.has(storeGroup.storeId)}>
              <CardBody pt={0}>
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                  {storeGroup.devices.map((device) => {
                    const assignmentStatus = getAssignmentStatus(device);
                    return (
                      <Card key={device.id} bg={cardBg} border="1px" borderColor={borderColor}>
                        <CardBody>
                          <VStack align="start" spacing={3}>
                            <Flex justify="space-between" align="center" w="full">
                              <VStack align="start" spacing={1}>
                                <Text fontWeight="bold" fontSize="md" color={textColor}>
                                  {device.name}
                                </Text>
                                <Text fontSize="sm" color="gray.500" fontFamily="mono">
                                  {device.identifier}
                                </Text>
                              </VStack>
                              <Badge colorScheme={getDeviceTypeColor(device.type?.name)}>
                                {device.type?.name || 'Unknown'}
                              </Badge>
                            </Flex>
                            
                            <Divider />
                            
                            <VStack align="start" spacing={2} w="full">
                              <Flex align="center" gap={2}>
                                <Icon as={MdAssignment} color="gray.500" boxSize={4} />
                                <Badge colorScheme={assignmentStatus.color} variant="subtle">
                                  {assignmentStatus.text}
                                </Badge>
                              </Flex>
                              
                              <Flex align="center" gap={2}>
                                <Icon as={MdDevices} color="gray.500" boxSize={4} />
                                <Text fontSize="sm" color="gray.600">
                                  {device.type?.name || 'Unknown Type'}
                                </Text>
                              </Flex>
                            </VStack>
                            
                            <Divider />
                            
                            <Flex justify="space-between" align="center" w="full">
                              <HStack spacing={2}>
                                <Tooltip label="Edit Device">
                                  <IconButton
                                    icon={<MdEdit />}
                                    size="sm"
                                    variant="ghost"
                                    colorScheme="blue"
                                    onClick={() => {
                                      // Handle edit
                                    }}
                                  />
                                </Tooltip>
                                <Tooltip label="Delete Device">
                                  <IconButton
                                    icon={<DeleteIcon />}
                                    size="sm"
                                    variant="ghost"
                                    colorScheme="red"
                                    onClick={() => handleDeleteClick(device.id)}
                                  />
                                </Tooltip>
                              </HStack>
                            </Flex>
                          </VStack>
                        </CardBody>
                      </Card>
                    );
                  })}
                </SimpleGrid>
              </CardBody>
            </Collapse>
          </Card>
        ))}
        
        {Object.keys(devicesByStore).length === 0 && (
          <Card bg={bgColor} border="1px" borderColor={borderColor}>
            <CardBody>
              <VStack spacing={4}>
                <Icon as={MdDevices} color="gray.400" boxSize={12} />
                <Text fontSize="lg" color="gray.500">
                  No devices found
                </Text>
                <Text fontSize="sm" color="gray.400" textAlign="center">
                  {searchQuery 
                    ? "Try adjusting your search criteria"
                    : "Get started by adding your first device"
                  }
                </Text>
              </VStack>
            </CardBody>
          </Card>
        )}
      </VStack>

      {/* Modal for Creating Device */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        isCentered
        size={{base: "sm", md: "md"}}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create New Device</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl id="device-name" mb="4">
              <FormLabel>Device Name</FormLabel>
              <Input
                value={deviceData.name}
                onChange={(e) =>
                  setDeviceData({ ...deviceData, name: e.target.value })
                }
                variant={"filled"}
                borderRadius={"full"}
              />
            </FormControl>
            <FormControl id="device-identifier" mb="4">
              <FormLabel>Device Identifier</FormLabel>
              <Input
                value={deviceData.identifier}
                onChange={(e) =>
                  setDeviceData({ ...deviceData, identifier: e.target.value })
                }
                variant={"filled"}
                borderRadius={"full"}
              />
            </FormControl>
            <FormControl id="device-type" mb="4">
              <FormLabel>Device Type</FormLabel>
              <Input
                value={deviceData.type}
                onChange={(e) =>
                  setDeviceData({ ...deviceData, type: e.target.value })
                }
                variant={"filled"}
                borderRadius={"full"}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button
              bg="black"
              color={"white"}
              _hover={{
                color: "black",
                bg: "white",
                borderWidth: "1px",
                borderColor: "gray.300",
              }}
              variant="outline"
              mr={3}
              onClick={handleModalSubmit}
            >
              Save
            </Button>
            <Button onClick={handleModalClose}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default DeviceAssignment;
